import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Chip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {
  AutocompleteInput,
  Datagrid,
  FunctionField,
  ListContextProvider,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  TextField,
  required,
  useGetList,
  useList,
  useNotify,
  useRecordContext,
  useRefresh,
} from "react-admin";
import dataProvider from "server/dataProvider";
import permissions from "app/permissions";
import SecuredComponent from "server/security/SecuredComponent";
import { settings } from "app";
import { doctoralRequests } from "api/doctoralApi";
import { employeeRequests } from "api/employeeApi";
import {
  DoctoralServicePath,
  DoctoralSchoolCampaignStatus,
  DoctoralSchoolDoctorantStatus,
  DoctoralSchoolResult,
} from "utils/constants";
import {
  DoctoralSchoolDoctorantOriginChoices,
  DoctoralSchoolDoctorantStatusChoices,
  DoctoralSchoolDoctorantStatusColorMap,
  DoctoralSchoolPaymentProviderChoices,
  DoctoralSchoolResultChoices,
  DoctoralSchoolResultColorMap,
} from "utils/choices";
import { useNotifyError } from "utils/useNotifyError";
import { formatDoctoralDate } from "../../Partials/DoctoralDateField";
import CertificateDialog from "./CertificateDialog";
import ResultDialog from "./ResultDialog";

const labelOf = (choices, id) => choices.find(c => c.id === id)?.name ?? id;

const AddFromDoctorantForm = ({ campaignId, excludedDoctorantIds = [], onDone }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const notifyError = useNotifyError();

  const { data: doctorants = [] } = useGetList(`${DoctoralServicePath}doctorants`, {
    pagination: { page: 1, perPage: 1000 },
  });

  const availableDoctorants = doctorants.filter(d => !excludedDoctorantIds.includes(d.id));

  const handleSave = async data => {
    try {
      await dataProvider.customRequest(
        doctoralRequests.createDoctoralSchoolDoctorantFromDoctorant(),
        { ...data, doctoralSchoolCampaignId: campaignId },
        "POST",
      );
      notify("Кандидатът е добавен успешно", { type: "success" });
      refresh();
      onDone();
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <SimpleForm
      toolbar={false}
      onSubmit={handleSave}
    >
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", width: "100%" }}>
        <AutocompleteInput
          source="doctorantId"
          label="Докторант"
          choices={availableDoctorants}
          optionText="fullName"
          sx={{ minWidth: 320 }}
          validate={required()}
        />
        <SelectInput
          source="paymentProviderId"
          label="Начин на плащане"
          choices={DoctoralSchoolPaymentProviderChoices}
          sx={{ minWidth: 240 }}
          validate={required()}
        />
      </Box>
      <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
        <Button
          variant="contained"
          type="submit"
          size="small"
        >
          Запази
        </Button>
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={onDone}
        >
          Откажи
        </Button>
      </Box>
    </SimpleForm>
  );
};

const AddTeacherForm = ({ campaignId, onDone }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const notifyError = useNotifyError();

  const handleSave = async data => {
    try {
      await dataProvider.customRequest(
        doctoralRequests.createTeacherDoctoralSchoolDoctorant(),
        { ...data, doctoralSchoolCampaignId: campaignId },
        "POST",
      );
      notify("Преподавателят е добавен успешно", { type: "success" });
      refresh();
      onDone();
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <SimpleForm
      toolbar={false}
      onSubmit={handleSave}
    >
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", width: "100%" }}>
        <ReferenceInput
          source="employeeId"
          reference={employeeRequests.getProfessorsSelect()}
          filter={{ excludeHonorary: true }}
        >
          <AutocompleteInput
            label="Преподавател"
            optionText={record => [record?.firstName, record?.lastName].filter(Boolean).join(" ")}
            filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
            validate={required("Задължително")}
            sx={{ minWidth: 320 }}
          />
        </ReferenceInput>
        <SelectInput
          source="paymentProviderId"
          label="Начин на плащане"
          choices={DoctoralSchoolPaymentProviderChoices}
          validate={required("Задължително")}
          sx={{ minWidth: 240 }}
        />
        <Button
          variant="contained"
          type="submit"
        >
          Запази
        </Button>
        <Button onClick={onDone}>Отказ</Button>
      </Box>
    </SimpleForm>
  );
};

const CandidatesSection = () => {
  const campaign = useRecordContext();
  const navigate = useNavigate();
  const [addMode, setAddMode] = useState(false);
  const [addTeacherMode, setAddTeacherMode] = useState(false);

  const { data: candidates = [], isLoading } = useGetList(
    `${DoctoralServicePath}doctoral-school-doctorants`,
    {
      pagination: { page: 1, perPage: 1000 },
      filter: { campaignId: campaign?.id },
    },
    { enabled: !!campaign?.id },
  );

  const listContext = useList({ data: candidates, isLoading });

  if (!campaign) return null;

  const canAdd = campaign.status === DoctoralSchoolCampaignStatus.OpenForEnrollment;

  return (
    <>
      <SecuredComponent permission={permissions.canManageDoctoralSchoolDoctorants}>
        {canAdd ? (
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 1 }}>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setAddMode(true)}
              disabled={addMode}
            >
              Добави от докторант
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setAddTeacherMode(true)}
              disabled={addTeacherMode}
            >
              Добави преподавател
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() =>
                navigate(`/${DoctoralServicePath}doctoral-school-external-candidate`, {
                  state: { doctoralSchoolCampaignId: campaign.id },
                })
              }
            >
              Добави външен кандидат
            </Button>
          </Box>
        ) : (
          <Typography
            variant="caption"
            sx={{ display: "block", textAlign: "right", mb: 1, color: "text.secondary" }}
          >
            Добавянето на кандидати е възможно само докато кампанията е отворена за записване.
          </Typography>
        )}
      </SecuredComponent>

      {addMode && (
        <Box sx={{ mb: 2 }}>
          <AddFromDoctorantForm
            campaignId={campaign.id}
            excludedDoctorantIds={candidates.map(c => c.sourceDoctorantId).filter(Boolean)}
            onDone={() => setAddMode(false)}
          />
        </Box>
      )}

      {addTeacherMode && (
        <Box sx={{ mb: 2 }}>
          <AddTeacherForm
            campaignId={campaign.id}
            onDone={() => setAddTeacherMode(false)}
          />
        </Box>
      )}

      {!isLoading && candidates.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ textAlign: "center", color: "text.secondary", py: 2 }}
        >
          Няма добавени кандидати.
        </Typography>
      ) : (
        <ListContextProvider value={listContext}>
          <Datagrid
            bulkActionButtons={false}
            rowClick={false}
            sx={settings.style.secondaryDatagrid}
          >
            <TextField
              source="fullName"
              label="Име"
              sortable={false}
            />
            <TextField
              source="personId"
              label="ЕГН/ЛНЧ"
              sortable={false}
            />
            <FunctionField
              label="Тип"
              sortable={false}
              render={c => labelOf(DoctoralSchoolDoctorantOriginChoices, c.origin)}
            />
            <FunctionField
              label="Статус"
              sortable={false}
              render={c => (
                <Chip
                  size="small"
                  label={labelOf(DoctoralSchoolDoctorantStatusChoices, c.status)}
                  color={DoctoralSchoolDoctorantStatusColorMap[c.status] ?? "default"}
                />
              )}
            />
            <FunctionField
              label="Платено на"
              sortable={false}
              render={c => formatDoctoralDate(c.paidOn, true) || "-"}
            />
            <FunctionField
              label="Резултат"
              sortable={false}
              render={c =>
                c.result ? (
                  <Chip
                    size="small"
                    label={labelOf(DoctoralSchoolResultChoices, c.result)}
                    color={DoctoralSchoolResultColorMap[c.result] ?? "default"}
                  />
                ) : (
                  "-"
                )
              }
            />
            <FunctionField
              label=""
              sortable={false}
              render={c =>
                c.status === DoctoralSchoolDoctorantStatus.Accepted ? (
                  <SecuredComponent permission={permissions.canRecordDoctoralSchoolResults}>
                    <ResultDialog
                      doctorantId={c.id}
                      currentResult={c.result}
                    />
                  </SecuredComponent>
                ) : null
              }
            />
            <FunctionField
              label="Сертификат"
              sortable={false}
              render={c =>
                c.certificateNumber
                  ? `${c.certificateNumber} / ${formatDoctoralDate(c.certificateIssuedOn)}`
                  : "-"
              }
            />
            <FunctionField
              label=""
              sortable={false}
              render={c =>
                c.result === DoctoralSchoolResult.Passed ? (
                  <SecuredComponent permission={permissions.canManageDoctoralSchoolDoctorants}>
                    <CertificateDialog
                      doctorantId={c.id}
                      hasCertificate={!!c.certificateNumber}
                    />
                  </SecuredComponent>
                ) : null
              }
            />
          </Datagrid>
        </ListContextProvider>
      )}
    </>
  );
};

export default CandidatesSection;
